import { useTranslation } from "react-i18next";

import { OrderTypes } from "@/lib/supabase/enums";
import {
  WEEKDAYS_MONDAY_FIRST,
  hasOpeningHourTimes,
  type OpeningHoursByDay,
} from "@/lib/opening-hours";
import { cn } from "@/lib/utils";
import { useFormattedOpeningHours } from "@/hooks/use-formatted-opening-hours";
import { useGetTranslatedDay } from "@/hooks/use-get-translated-day";
import { useGetTranslatedOrderType } from "@/hooks/use-get-translated-order-type";

export default function OpeningHoursList({
  openingHoursData,
  className,
}: {
  openingHoursData: OpeningHoursByDay;
  className?: string;
}) {
  const { t } = useTranslation("translation");
  const { getTranslatedDay } = useGetTranslatedDay();
  const { getTranslatedOrderType } = useGetTranslatedOrderType();
  const { sortedGroupedHours, formatDays } =
    useFormattedOpeningHours(openingHoursData);

  const hasHours = hasOpeningHourTimes(openingHoursData);

  return (
    <div className={cn("space-y-2 text-sm text-muted-foreground", className)}>
      {hasHours
        ? Object.entries(sortedGroupedHours).map(([orderType, timeGroups]) => (
            <div key={orderType} className="mb-3">
              <div className="font-medium text-foreground mb-1">
                {getTranslatedOrderType(orderType as OrderTypes)}
              </div>
              {Object.entries(timeGroups).map(([timeKey, days]) => (
                <div key={timeKey} className="flex justify-between">
                  <span>{formatDays(days)}</span>
                  <span>{timeKey}</span>
                </div>
              ))}
            </div>
          ))
        : WEEKDAYS_MONDAY_FIRST.map((day) => (
            <div key={day} className="flex justify-between">
              <span>{getTranslatedDay(day)}</span>
              <span>{t("closed")}</span>
            </div>
          ))}
    </div>
  );
}
